import React from 'react';
import styled, { css } from 'styled-components';

// Types
import { TypeInput, InputSizeTypes } from './types';

// Components
import { Typography } from '../../display/Typography';
import { VisibilityIcon } from '../../../assets/icons/VisibilityIcon';

type ContainerProps = {
  inputSize?: InputSizeTypes;
  typeInput: TypeInput;
};

type InputContainerProps = {
  typeInput: TypeInput;
  disabled: boolean | undefined;
  borderError: boolean | undefined;
  isFocused: boolean;
};
type InputValueProps = { typeInput: TypeInput };

// Helper function to get input size
const getInputMaxWidth = (inputSize?: InputSizeTypes): string => {
  if (!inputSize) return '22rem'; // Default M

  const predefinedSizes: Record<string, string> = {
    XXL: '38rem',
    XL: '35rem',
    L: '27rem',
    M: '22rem',
    S: '17rem',
    XS: '10rem',
  };

  // If it's a predefined size, return it
  if (predefinedSizes[inputSize]) {
    return predefinedSizes[inputSize];
  }

  // If it's a custom numeric string, append 'rem' if not already present
  if (
    !inputSize.includes('rem') &&
    !inputSize.includes('px') &&
    !inputSize.includes('%')
  ) {
    return `${inputSize}rem`;
  }

  // Return as is if it already has a unit
  return inputSize;
};

export const Container = styled.div<ContainerProps>`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs};
  max-width: ${({ inputSize }) => getInputMaxWidth(inputSize)};

  /* Override para email y password - siempre 100% */
  ${({ typeInput }) =>
    (typeInput === 'email' || typeInput === 'password') &&
    css`
      max-width: 100%;
    `};

  /* Responsive para tablet vertical */
  @media (max-width: ${({ theme }) => theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    max-width: 100%;
    /* 🔴 Aquí había un "flex: 1; min-width: 0". Como "flex: 1" es
       "flex: 1 1 0%", el tamaño hipotético del item era 0 y el "flex-wrap:
       wrap" del contenedor NO partía nunca la línea: los campos cabían todos
       y se repartían el hueco hasta quedarse en 12 px en Añadir usuario y
       18 px en Partidas presupuestarias (medido a 600 px), mientras los
       DropDownInput de al lado aguantaban su ancho.
       🔴 Y NO se arregla poniendo el tamaño en la base del flex: este
       contenedor se usa tanto en filas como en COLUMNAS (el formulario de
       login lo es), y en una columna el "flex-basis" es la ALTURA — cada
       campo pasaba a medir 220 px de alto. Por eso aquí no puede haber
       ninguna propiedad de flex: dependen del eje del padre, y este
       componente no sabe en cuál está.
       Sin ellas el ancho lo manda "width: 100%" acotado por el "max-width" de
       arriba, que es lo que ya hace DropDownInput: en una fila cada campo
       ocupa su línea completa en vez de estrujarse, y en una columna se queda
       con su alto natural. */
  }
`;

type labelProps = React.LabelHTMLAttributes<HTMLLabelElement>;

export const Label = styled(Typography)<labelProps>`
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const ErrorMessage = styled(Typography)``;

export const InputContainer = styled.div<InputContainerProps>`
  display: flex;
  align-items: center;
  height: ${({ theme }) => theme.sizes.buttonHeight};
  padding: 0 1rem;
  border: ${(props) =>
    !props.disabled ? `0.1rem solid ${props.theme.colors.black}` : 'none'};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: ${(props) =>
    props.borderError && `0.1rem solid ${props.theme.colors.lightCoralRed}`};
  outline: none;
  ${(props) =>
    !props.disabled &&
    css`
      background-color: ${props.theme.colors.white};
    `}

  ${(props) =>
    props.disabled &&
    css`
      background-color: ${props.theme.colors.greyLight};
    `}

    ${(props) =>
    props.isFocused &&
    css`
      outline: 0.1rem solid ${props.theme.colors.black};
    `}
`;

export const InputValue = styled.input<InputValueProps>`
  display: flex;
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  padding: 0;

  &::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: ${({ theme }) => theme.colors.greySoftLight};
    opacity: 1; /* Firefox */
  }

  &:-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: ${({ theme }) => theme.colors.greySoftLight};
  }

  &::-ms-input-placeholder {
    /* Microsoft Edge */
    color: ${({ theme }) => theme.colors.greySoftLight};
  }

  &::-ms-reveal {
    display: none;
  }

  ${(props) =>
    !props.disabled &&
    css`
      font-weight: ${props.theme.fontWeight.regular};
      color: ${props.theme.colors.black};
      background-color: ${props.theme.colors.white};
    `}

  ${(props) =>
    props.disabled &&
    css`
      font-weight: ${props.theme.fontWeight.medium};
      color: ${props.theme.colors.greyDark};
      background-color: ${props.theme.colors.greyLight};
    `}

  @media (max-width: ${({ theme }) =>
    theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const ContainerIconEye = styled.div`
  display: flex;
  align-items: center;
`;

export const IconEye = styled(VisibilityIcon)`
  cursor: pointer;

  path {
    fill: ${({ theme }) => theme.colors.greySoftLight};
  }
`;
